import React, { useRef, Children } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Star, Heart, Briefcase, Sparkles, Sun, Moon, Eye, FileText, Check, Clock, Shield, Target, Users, Lightbulb, TrendingUp, AlertTriangle, Zap, Calendar, BarChart3, Activity, Compass } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Breadcrumbs } from '../components/ui/Breadcrumbs';
import { Accordion } from '../components/ui/Accordion';
import { useCart } from '../contexts/CartContext';
interface ForecastTransitsPageProps {
  onNavigate: (page: string) => void;
}
const staggerContainer = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.08
    }
  }
};
const staggerItem = {
  hidden: {
    opacity: 0,
    y: 24
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.45,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};
function AnimatedSection({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <motion.section className={className} initial="hidden" whileInView="visible" viewport={{
    once: true,
    margin: '-60px'
  }} variants={{
    hidden: {
      opacity: 0,
      y: 40
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  }} data-id="element-22524">
      {children}
    </motion.section>;
}
export function ForecastTransitsPage({
  onNavigate
}: ForecastTransitsPageProps) {
  const ctaRef = useRef<HTMLDivElement>(null);
  const pricingRef = useRef<HTMLDivElement>(null);
  const {
    addItem
  } = useCart();
  const scrollToCta = () => {
    ctaRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };
  const scrollToPricing = () => {
    pricingRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };
  const handleBuyPremium = () => {
    addItem({
      id: `premium-forecast-${Date.now()}`,
      name: 'Premium отчёт: Прогноз (Транзиты)',
      description: 'Подробный астрологический прогноз с анализом всех транзитов, помесячным разбором и практическими рекомендациями.',
      price: 3900,
      type: 'premium-report',
      serviceName: 'Прогноз (Транзиты)',
      format: 'PDF-отчёт',
      duration: '25-35 страниц',
      badges: [{
        label: 'Premium',
        variant: 'pro'
      }]
    });
    onNavigate('checkout');
  };
  return <div className="w-full bg-slate-50 min-h-screen" data-id="element-22525">
      {/* ═══════════════════════════════════════════ */}
      {/* 1. HERO */}
      {/* ═══════════════════════════════════════════ */}
      <div className="relative bg-slate-900 text-white overflow-hidden pt-6 sm:pt-8 pb-14 sm:pb-24 lg:pb-32" data-id="element-22526">
        <div className="absolute top-0 right-0 -translate-y-1/4 translate-x-1/4 w-[800px] h-[800px] bg-teal-500/12 rounded-full blur-3xl pointer-events-none" data-id="element-22527" />
        <div className="absolute bottom-0 left-0 translate-y-1/3 -translate-x-1/4 w-[600px] h-[600px] bg-cyan-500/10 rounded-full blur-3xl pointer-events-none" data-id="element-22528" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-22529">
          <Breadcrumbs items={[{
          label: 'Главная',
          onClick: () => onNavigate('home')
        }, {
          label: 'Услуги',
          onClick: () => onNavigate('marketplace')
        }, {
          label: 'Прогноз (Транзиты)'
        }]} className="mb-6 sm:mb-12 text-slate-400 [&_span]:text-slate-300 [&_button]:text-slate-400 [&_button:hover]:text-white [&_svg]:text-slate-500" data-id="element-22530" />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center" data-id="element-22531">
            <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="max-w-2xl" data-id="element-22532">
              <motion.div variants={staggerItem} className="mb-4 sm:mb-5" data-id="element-22533">
                <Badge variant="pro" className="bg-teal-500/20 text-teal-300 border-teal-500/30 px-3 py-1 text-sm" data-id="element-22534">
                  Актуально
                </Badge>
              </motion.div>

              <motion.h1 variants={staggerItem} className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 sm:mb-5 leading-[1.15]" data-id="element-22535">
                Узнайте, какие события готовит для вас ближайшее будущее
              </motion.h1>

              <motion.p variants={staggerItem} className="text-sm sm:text-base md:text-lg text-slate-300 mb-6 sm:mb-8 leading-relaxed" data-id="element-22536">
                Астрологический анализ транзитов показывает, какие жизненные
                периоды активируются сейчас и какие события могут проявиться в
                ближайшее время.
              </motion.p>

              <motion.div variants={staggerItem} className="mb-6 sm:mb-10" data-id="element-22537">
                <Button size="lg" onClick={scrollToCta} className="text-base sm:text-lg px-6 sm:px-8 shadow-lg shadow-teal-500/20 bg-teal-500 hover:bg-teal-600" data-id="element-22538">
                  Получить персональный прогноз
                  <ArrowRight className="ml-2 w-5 h-5" data-id="element-22539" />
                </Button>
              </motion.div>

              <motion.div variants={staggerItem} className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-22540">
                {[{
                icon: <Activity className="w-4 h-4" data-id="element-22541" />,
                text: 'Понимание текущих периодов'
              }, {
                icon: <Calendar className="w-4 h-4" data-id="element-22542" />,
                text: 'Прогноз важных событий'
              }, {
                icon: <Zap className="w-4 h-4" data-id="element-22543" />,
                text: 'Благоприятные периоды'
              }, {
                icon: <Target className="w-4 h-4" data-id="element-22544" />,
                text: 'Рекомендации для решений'
              }].map((item, i) => <div key={i} className="flex items-center gap-2 text-slate-300 text-sm" data-id="element-22545">
                    <div className="w-7 h-7 rounded-lg bg-teal-500/20 flex items-center justify-center text-teal-300 flex-shrink-0" data-id="element-22546">
                      {item.icon}
                    </div>
                    {item.text}
                  </div>)}
              </motion.div>
            </motion.div>

            {/* Illustration — timeline / transit concept */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.8,
            ease: 'easeOut',
            delay: 0.2
          }} className="hidden lg:flex justify-center relative" data-id="element-22547">
              <div className="relative w-[420px] h-[420px]" data-id="element-22548">
                {/* Central natal chart */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[160px] h-[160px] rounded-full border border-teal-400/20 flex items-center justify-center" data-id="element-22549">
                  <div className="w-20 h-20 bg-gradient-to-br from-teal-400 to-cyan-500 rounded-full blur-2xl opacity-25" data-id="element-22550" />
                  <Star className="w-10 h-10 text-teal-300/60 absolute" strokeWidth={1} data-id="element-22551" />
                </div>
                {/* Orbiting transit planets */}
                {[{
                deg: 0,
                size: 'w-4 h-4',
                color: 'bg-amber-400',
                orbit: 38,
                speed: 25
              }, {
                deg: 45,
                size: 'w-3 h-3',
                color: 'bg-rose-400',
                orbit: 42,
                speed: 35
              }, {
                deg: 120,
                size: 'w-5 h-5',
                color: 'bg-teal-400',
                orbit: 35,
                speed: 60
              }, {
                deg: 180,
                size: 'w-3.5 h-3.5',
                color: 'bg-indigo-400',
                orbit: 45,
                speed: 45
              }, {
                deg: 240,
                size: 'w-3 h-3',
                color: 'bg-emerald-400',
                orbit: 40,
                speed: 30
              }, {
                deg: 300,
                size: 'w-4 h-4',
                color: 'bg-purple-400',
                orbit: 43,
                speed: 50
              }].map((planet, i) => <motion.div key={i} className="absolute top-1/2 left-1/2" animate={{
                rotate: [0, 360]
              }} transition={{
                duration: planet.speed,
                repeat: Infinity,
                ease: 'linear'
              }} style={{
                width: 0,
                height: 0
              }} data-id="element-22552">
                    <div className={`${planet.size} ${planet.color} rounded-full opacity-60 absolute`} style={{
                  transform: `translate(-50%, -50%) translateY(-${planet.orbit * 2.1}px) rotate(${planet.deg}deg)`
                }} data-id="element-22553" />
                  </motion.div>)}
                {/* Orbit rings */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] rounded-full border border-teal-500/10" data-id="element-22554" />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[360px] h-[360px] rounded-full border border-cyan-500/8" data-id="element-22555" />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] rounded-full border border-teal-400/5" data-id="element-22556" />
                {/* Aspect lines */}
                {[30, 75, 135].map((angle, i) => <div key={i} className="absolute top-1/2 left-1/2 w-40 h-px bg-gradient-to-r from-transparent via-teal-400/15 to-transparent" style={{
                transform: `translate(-50%, -50%) rotate(${angle}deg)`
              }} data-id="element-22557" />)}
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════ */}
      {/* 2. ЧТО ТАКОЕ ТРАНЗИТЫ */}
      {/* ═══════════════════════════════════════════ */}
      <AnimatedSection className="py-12 sm:py-20 bg-white" data-id="element-22558">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-22559">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-6 sm:mb-8 text-center" data-id="element-22560">
            Что такое транзиты
          </h2>
          <div className="prose prose-lg prose-slate max-w-none text-slate-600 space-y-6" data-id="element-22561">
            <p data-id="element-22562">
              Транзиты — это движение планет по небу в реальном времени и их
              взаимодействие с вашей натальной картой. Когда транзитная планета
              проходит определённую точку вашей карты рождения, она «активирует»
              соответствующую сферу жизни и может приносить изменения,
              возможности или испытания.
            </p>
            <p data-id="element-22563">
              Например, транзит Юпитера через ваш 10-й дом может принести
              карьерный рост и признание, а транзит Сатурна через 7-й дом —
              серьёзные уроки в отношениях. <strong data-id="element-22564">Медленные планеты</strong>{' '}
              (Юпитер, Сатурн, Уран, Нептун, Плутон) создают долгосрочные
              тенденции, а <strong data-id="element-22565">быстрые</strong> (Солнце, Луна, Меркурий,
              Венера, Марс) — краткосрочные события и настроения.
            </p>
            <p data-id="element-22566">
              Анализ транзитов помогает понять, какие энергии сейчас влияют на
              вашу жизнь, какие периоды наиболее благоприятны для действий и
              когда лучше проявить терпение. Это ваш персональный «навигатор» по
              текущим жизненным процессам.
            </p>
          </div>
        </div>
      </AnimatedSection>

      {/* ═══════════════════════════════════════════ */}
      {/* 3. ЧТО ПОКАЗЫВАЕТ АНАЛИЗ */}
      {/* ═══════════════════════════════════════════ */}
      <AnimatedSection className="py-12 sm:py-20 bg-slate-50" data-id="element-22567">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-22568">
          <div className="text-center max-w-3xl mx-auto mb-8 sm:mb-12" data-id="element-22569">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-3 sm:mb-4" data-id="element-22570">
              Что показывает анализ транзитов
            </h2>
            <p className="text-base sm:text-lg text-slate-600" data-id="element-22571">
              Шесть ключевых направлений вашего персонального прогноза
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-5xl mx-auto" data-id="element-22572">
            {[{
            icon: <TrendingUp className="w-5 h-5" data-id="element-22573" />,
            color: 'bg-teal-50 text-teal-600',
            title: 'Жизненные перемены',
            desc: 'Какие события могут проявиться в ближайшие месяцы, какие сферы жизни будут наиболее активны.'
          }, {
            icon: <Briefcase className="w-5 h-5" data-id="element-22574" />,
            color: 'bg-emerald-50 text-emerald-600',
            title: 'Карьера и финансы',
            desc: 'Периоды роста, новых возможностей, важных решений и потенциальных финансовых изменений.'
          }, {
            icon: <Heart className="w-5 h-5" data-id="element-22575" />,
            color: 'bg-rose-50 text-rose-600',
            title: 'Отношения',
            desc: 'Влияние текущих транзитов на личную жизнь, новые знакомства и динамику существующих связей.'
          }, {
            icon: <Sparkles className="w-5 h-5" data-id="element-22576" />,
            color: 'bg-indigo-50 text-indigo-600',
            title: 'Личностное развитие',
            desc: 'Какие уроки и задачи могут появиться в этот период, направления внутреннего роста.'
          }, {
            icon: <Zap className="w-5 h-5" data-id="element-22577" />,
            color: 'bg-amber-50 text-amber-600',
            title: 'Благоприятные периоды',
            desc: 'Когда лучше начинать новые проекты, принимать важные решения и действовать активно.'
          }, {
            icon: <AlertTriangle className="w-5 h-5" data-id="element-22578" />,
            color: 'bg-orange-50 text-orange-600',
            title: 'Сложные периоды',
            desc: 'Когда лучше проявлять осторожность, терпение и не рисковать деньгами или здоровьем.'
          }].map((item, i) => <motion.div key={i} variants={staggerItem} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} className="flex items-start gap-4 bg-white rounded-xl px-5 py-4 border border-slate-100" data-id="element-22579">
                <div className={`w-11 h-11 rounded-xl ${item.color} flex items-center justify-center flex-shrink-0`} data-id="element-22580">
                  {item.icon}
                </div>
                <div className="min-w-0" data-id="element-22581">
                  <h3 className="text-base font-bold text-slate-900 mb-1" data-id="element-22582">
                    {item.title}
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed" data-id="element-22583">
                    {item.desc}
                  </p>
                </div>
              </motion.div>)}
          </div>
        </div>
      </AnimatedSection>

      {/* ═══════════════════════════════════════════ */}
      {/* 4. КАК РАССЧИТЫВАЕТСЯ */}
      {/* ═══════════════════════════════════════════ */}
      <AnimatedSection className="py-12 sm:py-20 bg-white" data-id="element-22584">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-22585">
          <div className="text-center max-w-3xl mx-auto mb-8 sm:mb-12" data-id="element-22586">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-3 sm:mb-4" data-id="element-22587">
              Как рассчитывается прогноз транзитов
            </h2>
            <p className="text-base sm:text-lg text-slate-600" data-id="element-22588">
              Пять шагов от вашей карты до готового прогноза
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-5xl mx-auto" data-id="element-22589">
            {[{
            step: '01',
            icon: <Star className="w-5 h-5" data-id="element-22590" />,
            color: 'bg-teal-50 text-teal-600',
            stepColor: 'text-teal-500',
            title: 'Натальная карта',
            desc: 'Система анализирует вашу карту рождения'
          }, {
            step: '02',
            icon: <Activity className="w-5 h-5" data-id="element-22591" />,
            color: 'bg-indigo-50 text-indigo-600',
            stepColor: 'text-indigo-500',
            title: 'Текущие планеты',
            desc: 'Определяется положение планет прямо сейчас'
          }, {
            step: '03',
            icon: <Eye className="w-5 h-5" data-id="element-22592" />,
            color: 'bg-emerald-50 text-emerald-600',
            stepColor: 'text-emerald-500',
            title: 'Анализ аспектов',
            desc: 'Изучаются взаимодействия транзитов с натальной картой'
          }, {
            step: '04',
            icon: <Calendar className="w-5 h-5" data-id="element-22593" />,
            color: 'bg-amber-50 text-amber-600',
            stepColor: 'text-amber-500',
            title: 'Ключевые периоды',
            desc: 'Выявляются важные даты и окна возможностей'
          }, {
            step: '05',
            icon: <FileText className="w-5 h-5" data-id="element-22594" />,
            color: 'bg-rose-50 text-rose-600',
            stepColor: 'text-rose-500',
            title: 'Прогноз',
            desc: 'Формируется персональный астрологический прогноз'
          }].map((item, i) => <motion.div key={i} variants={staggerItem} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} className="flex items-center gap-4 bg-slate-50 rounded-xl px-5 py-4 border border-slate-100" data-id="element-22595">
                <div className={`w-11 h-11 rounded-xl ${item.color} flex items-center justify-center flex-shrink-0`} data-id="element-22596">
                  {item.icon}
                </div>
                <div className="min-w-0 flex-1" data-id="element-22597">
                  <div className="flex items-center gap-2 mb-0.5" data-id="element-22598">
                    <span className={`text-xs font-bold ${item.stepColor}`} data-id="element-22599">
                      {item.step}
                    </span>
                    <h3 className="text-base font-bold text-slate-900" data-id="element-22600">
                      {item.title}
                    </h3>
                  </div>
                  <p className="text-slate-500 text-sm" data-id="element-22601">{item.desc}</p>
                </div>
              </motion.div>)}
          </div>
        </div>
      </AnimatedSection>

      {/* ═══════════════════════════════════════════ */}
      {/* 5. ЧТО ВХОДИТ В ОТЧЁТ */}
      {/* ═══════════════════════════════════════════ */}
      <AnimatedSection className="py-12 sm:py-20 bg-slate-50" data-id="element-22602">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-22603">
          <div className="text-center max-w-3xl mx-auto mb-8 sm:mb-16" data-id="element-22604">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-3 sm:mb-4" data-id="element-22605">
              Что входит в отчёт прогноза
            </h2>
            <p className="text-base sm:text-lg text-slate-600" data-id="element-22606">
              Подробный документ с анализом текущих и предстоящих транзитов
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto" data-id="element-22607">
            {[{
            icon: <Activity className="w-5 h-5" data-id="element-22608" />,
            text: 'Анализ текущих транзитов'
          }, {
            icon: <Moon className="w-5 h-5" data-id="element-22609" />,
            text: 'Влияние медленных планет'
          }, {
            icon: <Star className="w-5 h-5" data-id="element-22610" />,
            text: 'Активные сферы жизни'
          }, {
            icon: <Calendar className="w-5 h-5" data-id="element-22611" />,
            text: 'Прогноз по месяцам'
          }, {
            icon: <Zap className="w-5 h-5" data-id="element-22612" />,
            text: 'Важные периоды и даты'
          }, {
            icon: <Compass className="w-5 h-5" data-id="element-22613" />,
            text: 'Рекомендации для действий'
          }, {
            icon: <Briefcase className="w-5 h-5" data-id="element-22614" />,
            text: 'Карьерные и финансовые тренды'
          }, {
            icon: <Heart className="w-5 h-5" data-id="element-22615" />,
            text: 'Прогноз по отношениям'
          }, {
            icon: <Shield className="w-5 h-5" data-id="element-22616" />,
            text: 'Зоны риска и предупреждения'
          }].map((item, i) => <div key={i} className="flex items-center gap-3 bg-white rounded-xl px-5 py-4 border border-slate-100" data-id="element-22617">
                <div className="w-10 h-10 rounded-xl bg-teal-50 text-teal-600 flex items-center justify-center flex-shrink-0" data-id="element-22618">
                  {item.icon}
                </div>
                <span className="text-sm font-medium text-slate-700" data-id="element-22619">
                  {item.text}
                </span>
              </div>)}
          </div>
        </div>
      </AnimatedSection>

      {/* ═══════════════════════════════════════════ */}
      {/* 6. ПРИМЕР ОТЧЁТА */}
      {/* ═══════════════════════════════════════════ */}
      <AnimatedSection className="py-14 sm:py-24 bg-white" data-id="element-22620">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-22621">
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl" data-id="element-22622">
            <div className="grid grid-cols-1 lg:grid-cols-2" data-id="element-22623">
              <div className="p-6 sm:p-10 lg:p-16 flex flex-col justify-center" data-id="element-22624">
                <span className="text-xs font-bold text-teal-300 uppercase tracking-[0.2em] mb-3 sm:mb-4" data-id="element-22625">
                  Пример отчёта
                </span>
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4 sm:mb-6 leading-tight" data-id="element-22626">
                  Конкретный прогноз, а не общие гороскопы
                </h2>
                <p className="text-slate-400 text-base sm:text-lg mb-6 sm:mb-8 leading-relaxed" data-id="element-22627">
                  Каждый отчёт содержит таблицу транзитов, график активных
                  периодов и подробный анализ влияния каждой планеты на вашу
                  жизнь.
                </p>
                <ul className="space-y-3 mb-6 sm:mb-10" data-id="element-22628">
                  {['Таблица текущих и предстоящих транзитов', 'График активных периодов по месяцам', 'Персональный прогноз по сферам жизни', 'Практические рекомендации'].map((t, i) => <li key={i} className="flex items-center text-slate-300 text-sm" data-id="element-22629">
                      <Check className="w-4 h-4 text-emerald-400 mr-3 flex-shrink-0" data-id="element-22630" />
                      {t}
                    </li>)}
                </ul>
                <Button size="lg" variant="outline" className="w-fit bg-white border-0 hover:bg-slate-100" onClick={() => onNavigate('report-preview-forecast')} data-id="element-22631">
                  Посмотреть пример PDF
                </Button>
              </div>
              <div className="bg-slate-800/50 p-4 sm:p-8 lg:p-12 flex items-center justify-center" data-id="element-22632">
                <motion.div animate={{
                y: [0, -6, 0]
              }} transition={{
                duration: 4,
                repeat: Infinity,
                ease: 'easeInOut'
              }} className="w-full max-w-sm bg-white rounded-xl shadow-2xl p-6" data-id="element-22633">
                  <div className="w-full h-10 bg-gradient-to-r from-teal-50 to-cyan-50 rounded-lg mb-5 flex items-center px-4" data-id="element-22634">
                    <div className="w-2 h-2 rounded-full bg-teal-400 mr-2" data-id="element-22635" />
                    <div className="w-20 h-2 bg-teal-200 rounded" data-id="element-22636" />
                    <div className="ml-auto w-12 h-2 bg-slate-200 rounded" data-id="element-22637" />
                  </div>
                  {/* Timeline chart */}
                  <div className="mb-5" data-id="element-22638">
                    <div className="flex items-center justify-between text-[9px] text-slate-400 mb-2" data-id="element-22639">
                      {['Янв', 'Фев', 'Мар', 'Апр', 'Май', 'Июн'].map(m => <span key={m} data-id="element-22640">{m}</span>)}
                    </div>
                    <div className="relative h-20" data-id="element-22641">
                      {/* Area chart simulation */}
                      <svg viewBox="0 0 200 60" className="w-full h-full" preserveAspectRatio="none" data-id="element-22642">
                        <defs data-id="element-22643">
                          <linearGradient id="tealGrad" x1="0" y1="0" x2="0" y2="1" data-id="element-22644">
                            <stop offset="0%" stopColor="#14b8a6" stopOpacity="0.3" data-id="element-22645" />
                            <stop offset="100%" stopColor="#14b8a6" stopOpacity="0.02" data-id="element-22646" />
                          </linearGradient>
                        </defs>
                        <motion.path d="M0,40 C20,35 40,15 60,20 C80,25 100,10 120,15 C140,20 160,30 180,12 L200,18 L200,60 L0,60 Z" fill="url(#tealGrad)" initial={{
                        opacity: 0
                      }} whileInView={{
                        opacity: 1
                      }} viewport={{
                        once: true
                      }} transition={{
                        duration: 1
                      }} data-id="element-22647" />
                        <motion.path d="M0,40 C20,35 40,15 60,20 C80,25 100,10 120,15 C140,20 160,30 180,12 L200,18" fill="none" stroke="#14b8a6" strokeWidth="2" initial={{
                        pathLength: 0
                      }} whileInView={{
                        pathLength: 1
                      }} viewport={{
                        once: true
                      }} transition={{
                        duration: 1.5,
                        ease: 'easeOut'
                      }} data-id="element-22648" />
                      </svg>
                      {/* Highlight dots */}
                      <div className="absolute top-[22%] left-[30%] w-2.5 h-2.5 rounded-full bg-teal-500 border-2 border-white shadow" data-id="element-22649" />
                      <div className="absolute top-[14%] left-[50%] w-2.5 h-2.5 rounded-full bg-amber-500 border-2 border-white shadow" data-id="element-22650" />
                      <div className="absolute top-[17%] left-[90%] w-2.5 h-2.5 rounded-full bg-teal-500 border-2 border-white shadow" data-id="element-22651" />
                    </div>
                  </div>
                  {/* Transit list */}
                  <div className="space-y-2.5" data-id="element-22652">
                    {[{
                    color: 'bg-teal-400',
                    w: '85%'
                  }, {
                    color: 'bg-amber-400',
                    w: '60%'
                  }, {
                    color: 'bg-rose-400',
                    w: '72%'
                  }].map((bar, i) => <div key={i} className="flex items-center gap-2" data-id="element-22653">
                        <div className={`w-2 h-2 rounded-full ${bar.color} flex-shrink-0`} data-id="element-22654" />
                        <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden" data-id="element-22655">
                          <motion.div className={`h-full rounded-full ${bar.color} opacity-60`} initial={{
                        width: 0
                      }} whileInView={{
                        width: bar.w
                      }} viewport={{
                        once: true
                      }} transition={{
                        duration: 1,
                        delay: 0.5 + i * 0.2
                      }} data-id="element-22656" />
                        </div>
                      </div>)}
                  </div>
                  <div className="mt-4 space-y-2" data-id="element-22657">
                    <div className="w-full h-1.5 bg-slate-100 rounded" data-id="element-22658" />
                    <div className="w-3/4 h-1.5 bg-slate-50 rounded" data-id="element-22659" />
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* ═══════════════════════════════════════════ */}
      {/* 7. ДЛЯ КОГО */}
      {/* ═══════════════════════════════════════════ */}
      <AnimatedSection className="py-12 sm:py-20 bg-slate-50" data-id="element-22660">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-22661">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-8 sm:mb-12 text-center" data-id="element-22662">
            Для кого подходит прогноз транзитов
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4" data-id="element-22663">
            {[{
            icon: <Activity className="w-5 h-5" data-id="element-22664" />,
            text: 'Людям, которые хотят понимать жизненные циклы'
          }, {
            icon: <Target className="w-5 h-5" data-id="element-22665" />,
            text: 'Тем, кто планирует важные решения и перемены'
          }, {
            icon: <Shield className="w-5 h-5" data-id="element-22666" />,
            text: 'Тем, кто хочет подготовиться к изменениям'
          }, {
            icon: <Star className="w-5 h-5" data-id="element-22667" />,
            text: 'Людям, интересующимся астрологией и прогнозами'
          }, {
            icon: <Briefcase className="w-5 h-5" data-id="element-22668" />,
            text: 'Предпринимателям для планирования бизнеса'
          }, {
            icon: <Lightbulb className="w-5 h-5" data-id="element-22669" />,
            text: 'Тем, кто хочет использовать время максимально эффективно'
          }].map((item, i) => <div key={i} className="flex items-center gap-4 bg-white rounded-xl px-5 py-4 border border-slate-100" data-id="element-22670">
                <div className="w-10 h-10 rounded-xl bg-teal-100 text-teal-600 flex items-center justify-center flex-shrink-0" data-id="element-22671">
                  {item.icon}
                </div>
                <span className="text-sm font-medium text-slate-700" data-id="element-22672">
                  {item.text}
                </span>
              </div>)}
          </div>
        </div>
      </AnimatedSection>

      {/* ═══════════════════════════════════════════ */}
      {/* 8. ПОЧЕМУ ВАЖНО ЗНАТЬ ТРАНЗИТЫ */}
      {/* ═══════════════════════════════════════════ */}
      <AnimatedSection className="py-12 sm:py-20 bg-white" data-id="element-22673">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-22674">
          <div className="text-center max-w-3xl mx-auto mb-8 sm:mb-16" data-id="element-22675">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-3 sm:mb-4" data-id="element-22676">
              Почему важно знать транзиты
            </h2>
            <p className="text-base sm:text-lg text-slate-600" data-id="element-22677">
              Анализ транзитов помогает лучше понимать текущие жизненные
              процессы и принимать более осознанные решения.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 max-w-4xl mx-auto" data-id="element-22678">
            {[{
            icon: <Eye className="w-6 h-6" data-id="element-22679" />,
            color: 'bg-teal-100 text-teal-600',
            title: 'Понимание текущих процессов',
            desc: 'Вы увидите, почему сейчас происходят определённые события и какие энергии влияют на вашу жизнь.'
          }, {
            icon: <Zap className="w-6 h-6" data-id="element-22680" />,
            color: 'bg-amber-100 text-amber-600',
            title: 'Использование возможностей',
            desc: 'Зная благоприятные периоды, вы сможете планировать важные действия в самое подходящее время.'
          }, {
            icon: <Shield className="w-6 h-6" data-id="element-22681" />,
            color: 'bg-rose-100 text-rose-600',
            title: 'Подготовка к сложностям',
            desc: 'Предупреждён — значит вооружён. Вы будете готовы к непростым периодам и сможете пройти их легче.'
          }, {
            icon: <Compass className="w-6 h-6" data-id="element-22682" />,
            color: 'bg-indigo-100 text-indigo-600',
            title: 'Осознанные решения',
            desc: 'Транзиты показывают, когда лучше действовать, а когда — выжидать. Это помогает избежать ошибок.'
          }].map((item, i) => <Card key={i} padding="lg" className="border-slate-100" data-id="element-22683">
                <div className="flex items-start gap-5" data-id="element-22684">
                  <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-2xl ${item.color} flex items-center justify-center flex-shrink-0`} data-id="element-22685">
                    {item.icon}
                  </div>
                  <div data-id="element-22686">
                    <h3 className="text-lg font-bold text-slate-900 mb-2" data-id="element-22687">
                      {item.title}
                    </h3>
                    <p className="text-slate-600 text-sm leading-relaxed" data-id="element-22688">
                      {item.desc}
                    </p>
                  </div>
                </div>
              </Card>)}
          </div>
        </div>
      </AnimatedSection>

      {/* ═══════════════════════════════════════════ */}
      {/* 9. CTA */}
      {/* ═══════════════════════════════════════════ */}
      <div ref={ctaRef} className="scroll-mt-10" data-id="element-22689">
        <AnimatedSection className="py-14 sm:py-24 bg-slate-900" data-id="element-22690">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-22691">
            <motion.div animate={{
            rotate: [0, 360]
          }} transition={{
            duration: 20,
            repeat: Infinity,
            ease: 'linear'
          }} data-id="element-22692">
              <Activity className="w-10 h-10 text-teal-400 mx-auto mb-4 sm:mb-6" data-id="element-22693" />
            </motion.div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4 sm:mb-6" data-id="element-22694">
              Получите персональный астрологический прогноз
            </h2>
            <p className="text-base sm:text-lg text-slate-400 mb-8 sm:mb-10 max-w-2xl mx-auto" data-id="element-22695">
              Введите данные рождения, чтобы получить анализ транзитов и прогноз
              ближайших жизненных периодов.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-22696">
              <Button size="lg" onClick={scrollToPricing} className="text-base sm:text-lg px-10 shadow-lg shadow-teal-500/20 bg-teal-500 hover:bg-teal-600" data-id="element-22697">
                Рассчитать прогноз
                <ArrowRight className="ml-2 w-5 h-5" data-id="element-22698" />
              </Button>
            </div>
            <div className="flex items-center justify-center gap-6 mt-8 text-sm text-slate-500" data-id="element-22699">
              <span className="flex items-center" data-id="element-22700">
                <Shield className="w-4 h-4 mr-1.5 text-emerald-500" data-id="element-22701" /> Данные
                защищены
              </span>
              <span className="flex items-center" data-id="element-22702">
                <Clock className="w-4 h-4 mr-1.5 text-teal-400" data-id="element-22703" /> Результат
                мгновенно
              </span>
            </div>
          </div>
        </AnimatedSection>
      </div>

      {/* ═══════════════════════════════════════════ */}
      {/* 10. ТАРИФЫ */}
      {/* ═══════════════════════════════════════════ */}
      <div ref={pricingRef} className="scroll-mt-10" data-id="element-22704">
        <AnimatedSection className="py-14 sm:py-24 bg-white" data-id="element-22705">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-22706">
            <div className="text-center mb-8 sm:mb-16" data-id="element-22707">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-3 sm:mb-4" data-id="element-22708">
                Выберите формат прогноза
              </h2>
              <p className="text-base sm:text-lg text-slate-600" data-id="element-22709">
                Два варианта — от краткого обзора до полного профессионального
                прогноза
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-8" data-id="element-22710">
              {/* Базовый */}
              <Card padding="lg" className="flex flex-col border-slate-200" data-id="element-22711">
                <Badge variant="neutral" className="w-fit mb-6" data-id="element-22712">
                  Базовый
                </Badge>
                <h3 className="text-2xl font-bold text-slate-900 mb-2" data-id="element-22713">
                  Базовый прогноз
                </h3>
                <p className="text-slate-500 mb-6 flex-grow" data-id="element-22714">
                  Краткий анализ текущих транзитов — основные тенденции,
                  ключевые влияния и общие рекомендации.
                </p>
                <ul className="space-y-3 sm:space-y-4 mb-6 sm:mb-8" data-id="element-22715">
                  {['Анализ основных транзитов', 'Влияние медленных планет', 'Ключевые периоды ближайших месяцев', 'Общие рекомендации', 'PDF-отчёт на 1-3 страницы', 'Готовность — мгновенно'].map((item, i) => <li key={i} className="flex items-start text-sm text-slate-600" data-id="element-22716">
                      <Check className="w-4 h-4 text-emerald-500 mr-2.5 mt-0.5 flex-shrink-0" data-id="element-22717" />
                      {item}
                    </li>)}
                </ul>
                <div className="text-2xl sm:text-3xl font-bold text-emerald-600 mb-6" data-id="element-22718">
                  Бесплатно
                </div>
                <Button variant="outline" fullWidth onClick={() => onNavigate('free-chart')} data-id="element-22719">
                  Получить базовый прогноз
                </Button>
              </Card>

              {/* Premium */}
              <Card padding="lg" className="flex flex-col border-teal-200 ring-1 ring-teal-100 shadow-xl shadow-teal-100/50 relative" data-id="element-22720">
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10 bg-teal-500 text-white text-xs font-bold px-4 py-1 rounded-full" data-id="element-22721">
                  Рекомендуем
                </div>
                <Badge variant="pro" className="w-fit mb-6" data-id="element-22722">
                  Полный
                </Badge>
                <h3 className="text-2xl font-bold text-slate-900 mb-2" data-id="element-22723">
                  Premium прогноз
                </h3>
                <p className="text-slate-500 mb-6 flex-grow" data-id="element-22724">
                  Подробный астрологический прогноз с анализом всех транзитов,
                  помесячным разбором и практическими рекомендациями.
                </p>
                <ul className="space-y-3 sm:space-y-4 mb-6 sm:mb-8" data-id="element-22725">
                  {['Всё из базового прогноза', 'Все транзиты медленных и быстрых планет', 'Помесячный прогноз', 'Карьера, финансы, отношения', 'Зоны риска и предупреждения', 'Практические рекомендации', 'PDF-отчёт на 25-35 страниц'].map((item, i) => <li key={i} className="flex items-start text-sm text-slate-600" data-id="element-22726">
                      <Check className="w-4 h-4 text-emerald-500 mr-2.5 mt-0.5 flex-shrink-0" data-id="element-22727" />
                      {item}
                    </li>)}
                </ul>
                <div className="text-2xl sm:text-3xl font-bold text-slate-900 mb-6" data-id="element-22728">
                  3 900 ₽
                </div>
                <Button variant="primary" fullWidth onClick={handleBuyPremium} className="bg-teal-500 hover:bg-teal-600" data-id="element-22729">
                  Получить Premium прогноз
                </Button>
              </Card>
            </div>

            <p className="text-center text-sm text-slate-500 mt-8 flex items-center justify-center gap-4" data-id="element-22730">
              <span className="flex items-center" data-id="element-22731">
                <Shield className="w-4 h-4 mr-1 text-emerald-500" data-id="element-22732" /> Безопасная
                оплата
              </span>
              <span className="flex items-center" data-id="element-22733">
                <Clock className="w-4 h-4 mr-1 text-teal-500" data-id="element-22734" /> Мгновенная
                доставка
              </span>
            </p>
          </div>
        </AnimatedSection>
      </div>

      {/* ═══════════════════════════════════════════ */}
      {/* FAQ */}
      {/* ═══════════════════════════════════════════ */}
      <AnimatedSection className="py-12 sm:py-20 bg-slate-50" data-id="element-22735">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-22736">
          <div className="text-center mb-8 sm:mb-12" data-id="element-22737">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3 sm:mb-4" data-id="element-22738">
              Частые вопросы
            </h2>
          </div>
          <Accordion items={[{
          id: '1',
          title: 'Чем прогноз транзитов отличается от соляра?',
          content: 'Соляр — это прогноз на год (от дня рождения до дня рождения), основанный на солярной карте. Прогноз транзитов анализирует текущее движение планет и их влияние на вашу натальную карту прямо сейчас. Транзиты дают более детальный и актуальный прогноз на ближайшие месяцы.'
        }, {
          id: '2',
          title: 'На какой период рассчитывается прогноз?',
          content: 'Базовый прогноз охватывает ближайшие 3-6 месяцев. Premium прогноз включает помесячный разбор на 6-12 месяцев вперёд с учётом всех значимых транзитов.'
        }, {
          id: '3',
          title: 'Нужно ли знать точное время рождения?',
          content: 'Для максимально точного прогноза желательно знать время рождения. Оно влияет на положение домов в натальной карте, а значит — на прогноз по конкретным сферам жизни. Без времени прогноз будет менее детальным.'
        }, {
          id: '4',
          title: 'Как часто стоит обновлять прогноз?',
          content: 'Рекомендуем обновлять прогноз каждые 3-6 месяцев, так как транзиты постоянно меняются. Особенно важно делать прогноз перед важными решениями — сменой работы, переездом, началом отношений.'
        }, {
          id: '5',
          title: 'Чем базовый прогноз отличается от Premium?',
          content: 'Базовый прогноз охватывает основные транзиты медленных планет и даёт общую картину (1-3 страницы). Premium включает все транзиты, помесячный разбор, детальные рекомендации по карьере, отношениям и финансам (25-35 страниц).'
        }]} data-id="element-22739" />
        </div>
      </AnimatedSection>
    </div>;
}